/** @jsxImportSource @emotion/react */
import { useCallback, useMemo } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import { Button } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import last from 'lodash/last'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'

import {
  FindAllReward,
  FindAllRewardVariables,
  FindAllReward_findAllReward_items,
} from '__generatedTypes__/FindAllReward'
import { UpdateReward, UpdateRewardVariables } from '__generatedTypes__/UpdateReward'
import { RewardTypeEnum } from '__generatedTypes__/globalTypes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { BULK_UPDATE_REWARDS, FIND_ALL_REWARDS, UPDATE_REWARD } from 'queries/rewards'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import Loader from 'components/Loader'
import Table from 'components/Table'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { bulkUpdateReward, bulkUpdateRewardVariables } from '__generatedTypes__/bulkUpdateReward'
import { pluralize } from 'utils/pluralize'

import { toast } from 'utils/toast'
import { ORDER_PARAM } from '../../consts'
import * as classes from '../styles'
import { MAP_ENUM_TO_TITLE } from './consts'
import { READ_ONLY_FOR_PLATFORMS } from './Form'

type Props = {
  platform: Platform
}

export const RewardsList = withPlatformCheck(({ platform }: Props) => {
  const apolloClient = useApolloClient()
  const { navigate, getLink } = useLink()
  const isReadOnly = READ_ONLY_FOR_PLATFORMS.includes(platform.slug as PlatformSlug)

  const findAllRewardsVariables = {
    query: {
      where: {
        platform: {
          id: platform.id,
        },
      },
      take: TAKE_ALL_ITEMS_COUNT,
    },
  }

  const { data, loading } = useQuery<FindAllReward, FindAllRewardVariables>(FIND_ALL_REWARDS, {
    fetchPolicy: 'network-only',
    variables: findAllRewardsVariables,
  })

  const [updateReward] = useMutation<UpdateReward, UpdateRewardVariables>(UPDATE_REWARD)
  const [bulkUpdateRewards] = useMutation<bulkUpdateReward, bulkUpdateRewardVariables>(BULK_UPDATE_REWARDS)

  const rewards = useMemo(() => {
    const items = data?.findAllReward.items ?? []
    return items.sort((a, b) => a.order - b.order)
  }, [data?.findAllReward])

  const handleSort = useCallback(async (rewardUpd: FindAllReward_findAllReward_items[]) => {
    try {
      const newOrder = rewardUpd.map((reward, order) => ({ ...reward, order }))

      apolloClient.writeQuery({
        query: FIND_ALL_REWARDS,
        variables: findAllRewardsVariables,
        data: {
          findAllReward: {
            ...data?.findAllReward,
            items: newOrder,
          },
        },
      })

      await bulkUpdateRewards({
        variables: {
          bulkUpdateInput: newOrder.map(({ id, order }) => ({ id, order })),
        },
      })

      toast.success('Rewards order updated successfully')
    } catch (error) {
      toast.error('Failed to update rewards order')
    }
  }, [])

  const handleIsLiveChange = async (id: string, isLive: boolean) => {
    try {
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'RewardType',
          id,
        }),
        fields: {
          isLive: () => isLive,
        },
      })

      await updateReward({
        variables: {
          updateInput: {
            id,
            isLive,
            platform: { id: platform.id },
          },
        },
      })

      toast.success('Reward updated successfully')
    } catch (error) {
      toast.error('Failed to update reward')
    }
  }

  if (loading) {
    return <Loader />
  }

  const lastRewardOrder = last(rewards)?.order ?? 0

  return (
    <div>
      <Table
        columns={[
          {
            title: 'Reward Type',
            renderCell: (reward) => {
              if (reward.type === RewardTypeEnum.Extension) {
                return `+${reward.value} ${pluralize(reward.value, 'Day', 'Days')} Extension`
              }
              return MAP_ENUM_TO_TITLE[reward.type]
            },
            flex: 2,
          },
          {
            title: 'Description',
            renderCell: (reward) => reward.description,
            flex: 2,
          },
          {
            title: '# within Accounts',
            renderCell: (reward) => <div>{reward.countOfEarnedRewards}</div>,
            flex: 2,
          },
          {
            title: 'Mystery Box %',
            renderCell: (reward) => <div>{reward.percentage}</div>,
            flex: 2,
          },
          {
            title: 'Live',
            renderCell: (reward) => (
              <Switch
                checked={reward.isLive}
                onClick={(e) => e.stopPropagation()}
                onChange={(_e, isChecked) => handleIsLiveChange(reward.id, isChecked)}
                disabled={isReadOnly}
              />
            ),
            flex: '0 1 60px',
          },
          {
            renderCell: (reward) => (
              <Link data-testid="edit-reward-link" to={getLink(cmsRoutes.REWARDS_EDIT, { rewardId: reward.id })}>
                <IconButton size="small">
                  <EditIcon />
                </IconButton>
              </Link>
            ),
            flex: '0 1 30px',
          },
        ]}
        items={rewards}
        onSort={handleSort}
        isSortable
      />
      <Button
        data-testid="add-reward-button"
        variant="outlined"
        size="large"
        color="blue"
        fullWidth
        css={classes.addButton}
        onClick={() =>
          navigate(cmsRoutes.REWARDS_CREATE, undefined, {
            searchParams: new URLSearchParams({
              [ORDER_PARAM]: String(lastRewardOrder),
            }),
          })
        }
        disabled={isReadOnly}
      >
        Add Reward <AddRoundedIcon />
      </Button>
    </div>
  )
})
